import React, {FC} from "react"
import {Radio, CustomNodeRenderer} from "@irati/ui/src/FormItem"
import styled, {css} from "styled-components"
import {Col} from "@irati/ui/src/ColumnRow"
import Flex from "@irati/ui/src/Flex"
import tracking from "@irati-tracking/perks/perks"
import {FormattedCurrency, FormattedMessage} from "@irati/utils/lang"
import {Body as TextBody, H6} from "@irati/ui/src/Typography"
import {Hr} from "@irati/ui/src/Layout"
import {IconTooltip} from "@irati/ui/src/Tooltip"
import {Deal} from "@irati/types"
import {OverlayPlacement} from "@irati/ui/src/Tooltip/Overlay"
import {useTheme} from "@irati/ui/src/global/useTheme"

import Badge from "@irati/ui/src/Badge"
import {SavingOptionEnum} from "."

interface OptionWrapperProps {
  hasIncreasedPointsBack: boolean
}

const OptionWrapper = styled.div<OptionWrapperProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 12px 8px 20px;
  ${({theme, hasIncreasedPointsBack}) => css`
    border-radius: ${theme.spacing.half};
    background-color: ${theme.colors.tertiary.o_50};
    margin-top: ${hasIncreasedPointsBack ? theme.spacing.three : theme.spacing.one};
  `}
`

const OptionLabel = styled.div`
  font-weight: bold;
  font-size: ${({theme}) => theme.spacing.two};
  color: ${({theme}) => theme.colors.greys.black};
`

const Body = styled(TextBody)`
  margin-top: ${({theme}) => theme.spacing.one};
`

const StyledTooltip = styled(IconTooltip)`
  margin: 5px 0 0 5px;
`

const StyledBadge = styled(Badge)`
  position: absolute;
  top: -16px;
  right: -10px;
`

interface Props {
  deal: Deal
  type: SavingOptionEnum
  name: string
  label: string
  isSelected: boolean
  onSelect: (value: SavingOptionEnum) => any
  amountToPay?: number
  currency?: string
  discount?: number
  cashbackFormulaDetails?: React.ReactNode
}

const SavingOption: FC<Props> = ({
  deal,
  type,
  name,
  label,
  isSelected,
  onSelect,
  discount,
  amountToPay,
  currency,
  cashbackFormulaDetails
}) => {
  const theme = useTheme()
  const optionNodes = [
    {
      customNode: <OptionLabel>{label}</OptionLabel>,
      value: type,
      whiteSpace: "wrap"
    }
  ]

  const hasIncreasedPointsBack = type === SavingOptionEnum.FlexiPointsCashback && deal.increasedPointsBackPercentageDelta

  return (
    <OptionWrapper hasIncreasedPointsBack={!!hasIncreasedPointsBack}>
      {!!hasIncreasedPointsBack && (
        <StyledBadge bgColor={theme.colors.utility.acid} color={theme.colors.greys.black}>
          <H6>
            <FormattedMessage id="perks.components.orderSummary.boostedPointsBack" />
          </H6>
        </StyledBadge>
      )}
      <Col md={12}>
        <Flex alignItems="center" gap={0.5}>
          <Radio
            rowRenderer={CustomNodeRenderer}
            name={name}
            value={isSelected && type}
            options={optionNodes}
            onChange={() => onSelect(type)}
          />
          {!!hasIncreasedPointsBack && (
            <IconTooltip placement={OverlayPlacement.Bottom} onMouseEnter={() => tracking.hoverCashbackTooltip(deal)} icon="i">
              {cashbackFormulaDetails}
            </IconTooltip>
          )}
        </Flex>
      </Col>
      <Col md={12}>
        {!hasIncreasedPointsBack && (
          <>
            <Flex direction="row" justifyContent="space-between" marginTop={2} alignItems="center">
              {type === SavingOptionEnum.Discount ? (
                <>
                  <Body small>
                    <FormattedMessage id="perks.components.orderSummary.subtotal.discount" />
                  </Body>
                  <Body small style={{marginLeft: 5}}>
                    <FormattedCurrency value={discount} currency={currency} />
                  </Body>
                </>
              ) : (
                <>
                  <Body small>
                    <FormattedMessage id="perks.components.orderSummary.total.withVoucher" />
                  </Body>
                  <StyledTooltip placement={OverlayPlacement.Bottom} onMouseEnter={() => tracking.hoverCashbackTooltip(deal)} icon="i">
                    {cashbackFormulaDetails}
                  </StyledTooltip>
                </>
              )}
            </Flex>
            <Hr marginTop="10px" marginBottom="10px" />
          </>
        )}
        <Flex direction="row" justifyContent="space-between">
          <Body small>
            <FormattedMessage id="perks.components.orderSummary.total.toPay" />
          </Body>
          <Body small style={{marginLeft: 5}}>
            <FormattedCurrency value={amountToPay} currency={currency} />
          </Body>
        </Flex>
      </Col>
    </OptionWrapper>
  )
}

export default SavingOption
